import React, {useContext, useEffect, useState} from "react";
import {AiAttributeServiceContext, AppContextContext, OrganizationServiceContext} from "../../Contexts";
import {Alert, Breadcrumb, Button, Descriptions, Form, Input, message, Modal, Select, Skeleton, Spin, Tag} from "antd";
import {LoadingOutlined} from "@ant-design/icons";
import Collapse from "@kunukn/react-collapse";
import * as globalStyles from "../App.module.css";
import * as styles from "./AiAttributeDetail.module.css";
import {Link, useNavigate, useParams} from "react-router-dom";
import {DocumentTitle} from "../DocumentTitle";
import AiAttribute, {AI_ATTRIBUTE_RESULT_TYPES, AiAttributeResultType, describeAiAttributeResultType, describeAiAttributeValueRenderType, formatAiAttributeResultType, formatAiAttributeValueRenderType} from "../../domain/AiAttribute";
import {useForm, useWatch} from "antd/es/form/Form";
import {ResourceOwnerType} from "../../domain/ResourceOwnerType";
import Organization from "../../domain/Organization";
import QueryOptions from "../../sal-ui/QueryOptions";
import {Permission} from "../../domain/auth/Permission";
import * as descriptionStyles from "../SelectItemDescription.module.css";

function AiAttributeDetail() {
    const appContext = useContext(AppContextContext);
    const aiAttributeService = useContext(AiAttributeServiceContext);
    const organizationService = useContext(OrganizationServiceContext);
    const navigate = useNavigate();
    const {aiAttributeId} = useParams<{ aiAttributeId: string }>();
    const [aiAttribute, setAiAttribute] = useState<AiAttribute | undefined>();
    const hasManage = appContext.user.isSystemAdmin() || appContext.user.hasPermission(Permission.AI_ATTRIBUTE__MANAGE_ORG);
    const [editMode, setEditMode] = useState(false);
    const [form] = useForm();
    const resultType = useWatch("resultType", form);
    const owner = useWatch("owner", form);
    const ownershipEditable = !aiAttributeId;
    const [organizations, setOrganizations] = useState<Organization[]>();
    const showValueRenderType = isValueRenderTypeConfigurable(resultType);

    const layout = {
        labelCol: {span: 6},
        wrapperCol: {span: 18},
    };

    const tailLayout = {
        wrapperCol: {offset: 6, span: 18},
    };

    const title = aiAttributeId ? (aiAttribute?.name || "AI attribute") : "New AI attribute";
    const managedByInterest = !!aiAttribute?.interestId;
    const canDirectlyManageAttribute = hasManage
        && !managedByInterest
        && (appContext.user.isSystemAdmin() || aiAttribute?.owner === ResourceOwnerType.ORGANIZATION);
    const showEditForm = !managedByInterest && (!aiAttributeId || editMode);

    useEffect(() => {
        if (appContext.user.isSystemAdmin()) {
            organizationService.getList(QueryOptions.newUnlimitedOrderedInstance("name")).then(value => setOrganizations(value.data));
        }

        if (aiAttributeId) {
            setEditMode(false);
            reload();
        } else {
            form.setFieldsValue({resultType: "VALUE_RATING", valueRenderType: "TEXT", allowedValues: [], owner: ResourceOwnerType.SYSTEM});
            setEditMode(true);
        }
    }, [aiAttributeId]);

    if (aiAttributeId && !aiAttribute) {
        return <Skeleton/>;
    }

    return (
        <DocumentTitle title={title}>
            <>
                <Breadcrumb className={globalStyles["common__breadcrumb"]}
                            items={[
                                {title: appContext.config?.appName},
                                {title: <Link to={"/definitions/ai-attributes"}>AI attributes</Link>},
                                {title}
                            ]}
                />

                <Spin
                    spinning={aiAttributeId ? !aiAttribute : false}
                    indicator={<LoadingOutlined style={{fontSize: 24}} spin={true}/>}
                >
                    <h1>{title}</h1>

                    {managedByInterest &&
                        <Alert
                            className={styles.notice}
                            type="info"
                            showIcon
                            title="Automatically managed by Interests"
                            description={
                                <>
                                    This AI attribute is generated from a linked interest and cannot be edited directly here.
                                    {" "}
                                    <Link to={`/interests/${aiAttribute.interestId}`}>Open the linked interest</Link>.
                                </>
                            }
                        />
                    }

                    {aiAttributeId && canDirectlyManageAttribute &&
                        <div className={globalStyles["common__top-button-bar"]}>
                            <>
                                <Button type={"primary"} onClick={() => setEditMode(!editMode)}>
                                    Edit
                                </Button>
                                <Button
                                    danger
                                    style={{marginLeft: 8}}
                                    onClick={() => {
                                        Modal.confirm({
                                            content: "Do you really want to delete this AI attribute?",
                                            okText: "Delete",
                                            cancelText: "Cancel",
                                            okButtonProps: {danger: true},
                                            onOk: () => onDelete()
                                        });
                                    }}>
                                    Delete
                                </Button>
                            </>
                        </div>
                    }

                    <Collapse isOpen={showEditForm}>
                        {aiAttributeId && <h3>Edit AI attribute</h3>}

                        <Form
                            {...layout}
                            form={form}
                            onFinish={onFinish}
                            className={`${globalStyles["common__form"]} ${aiAttributeId ? globalStyles["common_form--edit"] : globalStyles["common_form--add"]}`}
                        >
                            <Form.Item name="name" label="Name" rules={[{required: true, message: "Name is required."}]}>
                                <Input maxLength={100}/>
                            </Form.Item>

                            {appContext.user.isSystemAdmin() && <>
                                <Form.Item
                                    name="owner"
                                    label="Owner"
                                    rules={[{required: true, message: "Owner is required."}]}
                                >
                                    <Select
                                        disabled={!ownershipEditable}
                                        options={[{
                                            value: ResourceOwnerType.SYSTEM,
                                            label: "system"
                                        }, {
                                            value: ResourceOwnerType.ORGANIZATION,
                                            label: "organization"
                                        }]}
                                    />
                                </Form.Item>

                                {owner === ResourceOwnerType.ORGANIZATION && <Form.Item
                                    name="organization"
                                    label="Organization"
                                    rules={[{required: true, message: "Organization is required."}]}>
                                    <Select
                                        allowClear={true}
                                        disabled={!ownershipEditable}
                                        options={organizations?.map(organization => ({value: organization.id, label: organization.name}))}
                                    />
                                </Form.Item>}
                            </>}

                            <Form.Item
                                name="resultType"
                                label="Result type"
                                rules={[{required: true, message: "Result type is required."}]}
                            >
                                <Select optionLabelProp="label">
                                    {AI_ATTRIBUTE_RESULT_TYPES.map(resultType => (
                                        <Select.Option key={resultType} value={resultType} label={formatAiAttributeResultType(resultType)}>
                                            {formatAiAttributeResultType(resultType)}
                                            <span className={descriptionStyles.description}> - {describeAiAttributeResultType(resultType)}</span>
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            {resultType === "VALUE_TEXT" &&
                                <Form.Item
                                    name="allowedValues"
                                    label="Allowed values"
                                    extra="Optional. If filled, AI extractor output will be limited to these exact text values."
                                >
                                    <Select
                                        mode="tags"
                                        tokenSeparators={[","]}
                                        placeholder="Add allowed values"
                                        options={(form.getFieldValue("allowedValues") || []).map((value: string) => ({
                                            value,
                                            label: value
                                        }))}
                                    />
                                </Form.Item>
                            }

                            {showValueRenderType &&
                                <Form.Item
                                    name="valueRenderType"
                                    label="Value render type"
                                    rules={[{required: true, message: "Value render type is required."}]}
                                    extra="How the text values should be rendered in the UI."
                                >
                                    <Select optionLabelProp="label">
                                        {(["TEXT", "CVE", "URL"] as const).map(renderType => (
                                            <Select.Option key={renderType} value={renderType} label={formatAiAttributeValueRenderType(renderType)}>
                                                {formatAiAttributeValueRenderType(renderType)}
                                                <span className={descriptionStyles.description}> - {describeAiAttributeValueRenderType(renderType)}</span>
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            }

                            <Form.Item
                                name="prompt"
                                label="Prompt"
                                rules={[{required: true, message: "Prompt is required."}]}
                            >
                                <Input.TextArea rows={10} maxLength={10000}/>
                            </Form.Item>

                            <Form.Item {...tailLayout} className={globalStyles["common__form-buttons"]}>
                                <Button type="primary" htmlType="submit">
                                    Save
                                </Button>
                                <Button
                                    style={{marginLeft: 8}}
                                    onClick={() => {
                                        if (aiAttributeId) {
                                            setEditMode(false);
                                            if (aiAttribute) {
                                                form.setFieldsValue({
                                                    ...aiAttribute,
                                                    organization: aiAttribute.organization?.id
                                                });
                                            }
                                        } else {
                                            navigate("/definitions/ai-attributes");
                                        }
                                    }}>
                                    Cancel
                                </Button>
                            </Form.Item>
                        </Form>
                    </Collapse>

                    {aiAttributeId && aiAttribute &&
                        <Descriptions column={1}
                                      bordered={true}
                                      size="small"
                                      className={`${globalStyles.details}`}>
                            <Descriptions.Item label="Name">{aiAttribute.name}</Descriptions.Item>
                            <Descriptions.Item label="Result type">
                                {formatAiAttributeResultType(aiAttribute.resultType)}
                                {isValueRenderTypeConfigurable(aiAttribute.resultType) &&
                                    <> (rendered as <i>{formatAiAttributeValueRenderType(aiAttribute.valueRenderType)}</i>)</>
                                }
                            </Descriptions.Item>
                            <Descriptions.Item label="Prompt">
                                {aiAttribute.prompt ? <div className={styles.prompt}>{aiAttribute.prompt}</div> : <i>no prompt</i>}
                            </Descriptions.Item>
                            {aiAttribute.resultType === "VALUE_TEXT" &&
                                <Descriptions.Item label="Allowed values">
                                    {aiAttribute.allowedValues && aiAttribute.allowedValues.length > 0
                                        ? aiAttribute.allowedValues.map(value => <Tag key={value}>{value}</Tag>)
                                        : <i>all values allowed</i>}
                                </Descriptions.Item>
                            }
                            {aiAttribute.owner === ResourceOwnerType.ORGANIZATION &&
                                <Descriptions.Item label="Owner">{aiAttribute.organization?.name}</Descriptions.Item>}
                            {aiAttribute.owner === ResourceOwnerType.SYSTEM &&
                                <Descriptions.Item label="Owner"><i>System attribute</i></Descriptions.Item>}
                            {managedByInterest &&
                                <Descriptions.Item label="Management">
                                    Automatically managed from{" "}
                                    <Link to={`/interests/${aiAttribute.interestId}`}>the linked interest</Link>.
                                </Descriptions.Item>
                            }
                        </Descriptions>
                    }
                </Spin>
            </>
        </DocumentTitle>
    );

    function reload() {
        if (!aiAttributeId) return;

        aiAttributeService.get(aiAttributeId)
            .then(value => {
                setAiAttribute(value);

                form.setFieldsValue({
                    ...value,
                    organization: value.organization?.id
                });
            });
    }

    function onFinish(values: any) {
        const normalizedValues = normalizeFormValues(values);

        if (aiAttributeId) {
            aiAttributeService.update(aiAttributeId, normalizedValues)
                .then(() => {
                    message.success("AI attribute updated.");

                    setEditMode(false);

                    reload();
                });
        } else {
            aiAttributeService.add(normalizedValues)
                .then(id => {
                    message.success("AI attribute added.");

                    navigate(`/definitions/ai-attributes/${id}`);
                });
        }
    }

    function normalizeFormValues(values: any) {
        if (isValueRenderTypeConfigurable(values.resultType)) {
            return values;
        }

        return {
            ...values,
            valueRenderType: "TEXT"
        };
    }

    function onDelete() {
        if (!aiAttribute) return;

        aiAttributeService.delete(aiAttribute).then(() => {
            message.success("AI attribute deleted.");

            navigate("/definitions/ai-attributes");
        });
    }
}

function isValueRenderTypeConfigurable(resultType?: AiAttributeResultType | string) {
    return resultType === "VALUE_TEXT" || resultType === "VALUE_TEXTS";
}

export default AiAttributeDetail;
